import { useEffect, useMemo, useState } from "react";
import { BooleanText } from "../../api/types";
import { DataInputFormField, InputTypes } from "../../types";
import Input from "./Input";
import InfoIconWithTooltip from "../InfoIconWithTooltip";
import { twMerge } from "tailwind-merge";
import _ from "lodash";

export default function LabeledInput({
    onChange,
    onSecondaryChange,
    className = "w-full",
    dataInputFormProps,
    isSmallLabel = false,
    withoutLabel = false,
    withoutTooltip = false,
}: {
    dataInputFormProps: DataInputFormField;
    valueToDisplay?: string;
    onChange: (value: string | number | BooleanText | null) => void;
    onSecondaryChange?: (value: string | number | BooleanText | null) => void;
    originalValue?: string | number;
    secondaryValue?: number | null;
    tooltipText?: string;
    isSmallInput?: boolean;
    isSmallLabel?: boolean;
    withoutLabel?: boolean;
    withoutTooltip?: boolean;
    className?: string;
}) {
    const [isDirty, setIsDirty] = useState(false);

    const {
        id,
        value,
        label,
        required,
        secondaryError,
        disabled,
        originalValue,
        withSecondaryValue,
        isSecondaryInputEditable,
        tooltipText,
        isSmallInput = false,
    } = dataInputFormProps;
    const secondaryValue =
        "secondaryValue" in dataInputFormProps
            ? dataInputFormProps.secondaryValue
            : undefined;

    useEffect(() => {
        setIsDirty(!_.isEqual(value, originalValue));
    }, [value, originalValue]);

    const isSecondaryValueMode = useMemo(
        () => Boolean(secondaryValue || withSecondaryValue),
        [secondaryValue, withSecondaryValue]
    );

    const inputConfig = {
        onChange,
        isDirty,
        disabled,
    };
    const secondaryInputConfig = {
        onChange: isSecondaryInputEditable ? onSecondaryChange : undefined,
        isDirty,
        disabled: !isSecondaryInputEditable,
        hideError:
            Boolean(dataInputFormProps.error) || !isSecondaryInputEditable,
    };

    const secondaryDataInputFormProps: DataInputFormField = {
        ...dataInputFormProps,
        error: secondaryError,
        id: id + "_secondary",
    };

    secondaryDataInputFormProps.value = secondaryValue ? secondaryValue : "";
    secondaryDataInputFormProps.required = false;

    return (
        <div
            className={`${className} flex ${
                isSecondaryValueMode ? "gap-2 xl:gap-4" : "gap-1.5 xl:gap-2.5"
            } h-6 xl:h-[30px] items-center justify-between`}
        >
            <Label
                {...{
                    label: label ?? "",
                    id,
                    isSmallLabel,
                    isSecondaryValueMode,
                    isSmallInput,
                    withoutLabel,
                    required,
                }}
            />

            <div
                className={`${
                    withoutLabel || isSmallLabel ? "w-full" : "xl:w-[204px]"
                } ${isSecondaryValueMode ? "" : "gap-0 xl:gap-2"}
                     h-full justify-end flex`}
            >
                <Input
                    className={twMerge(
                        "shrink-0",
                        isSmallLabel && "w-[40px] xl:w-[60px]",
                        isSecondaryValueMode || isSmallInput
                            ? "w-[58px] xl:w-[90px]"
                            : "w-[106px] xl:w-[160px]",
                        isSecondaryValueMode && "mr-1 xl:mr-4"
                    )}
                    inputClassName={
                        isSecondaryValueMode || isSmallInput
                            ? "text-center"
                            : "text-start"
                    }
                    dataInputFormProps={dataInputFormProps}
                    {...inputConfig}
                />
                {isSecondaryValueMode ? (
                    <Input
                        dataInputFormProps={secondaryDataInputFormProps}
                        inputClassName="text-center"
                        className="w-[58px] xl:w-[90px] shrink-0 items-end"
                        {...secondaryInputConfig}
                    />
                ) : null}
                {!withoutTooltip ? (
                    <InfoIconWithTooltip
                        hideIcon={!isDirty}
                        tooltipContent={
                            tooltipText ? (
                                tooltipText
                            ) : (
                                <div>
                                    <span className="block whitespace-nowrap">
                                        This field has been edited.
                                    </span>
                                    Previous value: {originalValue}
                                </div>
                            )
                        }
                    />
                ) : null}
            </div>
        </div>
    );
}

const Label = ({
    label,
    id,
    isSmallLabel,
    isSecondaryValueMode,
    isSmallInput,
    withoutLabel,
    required = false,
}: {
    label: string;
    id: string;
    isSmallLabel: boolean;
    isSecondaryValueMode: boolean;
    isSmallInput: boolean;
    withoutLabel: boolean;
    required?: boolean;
}) => {
    return withoutLabel ? null : (
        <label
            className={`font-semibold shrink-0 text-[13px] xl:text-base`}
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: label + (required ? "*" : "") }}
        />
    );
};
